<template>
  <Container indent="wide" class="mb-20 mt-16">
    <p class="mb-2 text-sm text-blue-dark-900/60">
      {{ $t('filter.filter_by') }}
    </p>

    <div
      class="flex flex-col justify-between space-y-6 lg:flex-row lg:items-start lg:space-y-0 xl:items-center"
    >
      <div
        class="grid grid-cols-1 gap-4 md:grid-cols-2 xl:flex xl:w-full xl:pr-4"
      >
        <!-- SECTIONS -->
        <AppMultiselect
          v-model="filter.section"
          :options="sections"
          class="min-w-[13rem]"
          mode="multiple"
          label="label"
          :placeholder="$t('job_categories')"
        >
          <template #icon>
            <SuitCaseIcon class="w-[1.125rem]" />
          </template>
        </AppMultiselect>

        <!-- LOCATIONS -->
        <AppMultiselect
          v-model="filter.location"
          :options="locations"
          mode="multiple"
          label="label"
          class="min-w-[13rem]"
          :placeholder="$t('job_region')"
        >
          <template #icon>
            <MarkerIcon class="w-[0.875rem] text-blue-800" />
          </template>
        </AppMultiselect>

        <!-- TYPES -->
        <AppMultiselect
          v-model="filter.type"
          :options="types"
          mode="multiple"
          label="label"
          class="min-w-[13rem]"
          :placeholder="$t('job_type')"
        >
          <template #icon>
            <PenIcon class="w-4" />
          </template>
        </AppMultiselect>

        <!-- SORT SELECT -->
        <div
          class="relative flex h-12 w-auto min-w-[13rem] lg:max-w-[16rem] xl:w-full xl:max-w-none xl:justify-end"
        >
          <select
            v-model="filter.sort"
            :aria-label="$t('accessibility.sort_entries')"
            class="w-full appearance-none rounded-sm bg-[#F0FAFF] px-4 md:bg-transparent"
          >
            <option value="date desc">{{ $t('filter.date_desc') }}</option>
            <option value="date asc">{{ $t('filter.date_asc') }}</option>
          </select>
          <CaretRightIcon
            stroke-width="2"
            class="absolute right-5 top-1/2 w-[0.375rem] -translate-y-1/2 rotate-90 text-blue-800"
          />
        </div>
      </div>
      <!-- TEXT SEARCH -->
      <SearchInput
        v-model="filter.text"
        class="hidden w-full min-w-[13rem] sm:block lg:max-w-[20rem]"
        small
      />
      <!-- TEXT SEARCH MOBILE -->
      <div
        class="relative min-h-[1.625rem] w-full min-w-[13rem] sm:hidden lg:max-w-[20rem]"
      >
        <TransitionExpand
          class="w-full overflow-hidden transition-all delay-200 duration-300 ease-in-out"
          :open="isShowingMore"
        >
          <SearchInput
            v-model="filter.text"
            class="w-full min-w-[13rem] lg:max-w-[20rem]"
            small
          />
        </TransitionExpand>
        <transition name="fade">
          <button
            v-show="!isShowingMore"
            class="absolute left-0 top-0 flex items-center gap-2 text-base text-blue-800"
            @click="isShowingMore = true"
          >
            <PlusIcon class="w-[0.625rem] shrink-0" />
            <span>{{ $t('show_more') }}</span>
          </button>
        </transition>
      </div>
    </div>

    <TransitionGroup
      :key="rerenderKey"
      tag="ul"
      name="list"
      mode="out-in"
      class="relative mt-14 space-y-4"
    >
      <li v-for="job in jobs" :key="job.id" class="w-full">
        <JobCard :data="job" />
      </li>
    </TransitionGroup>

    <!-- SHOW MORE -->
    <div v-show="hasMorePages" class="mt-14 flex w-full justify-center">
      <AppButton @click="showMore()">
        {{ $t('filter.load_more') }}
      </AppButton>
    </div>

    <!-- NO ENTRIES FOUND -->
    <transition name="fade">
      <div
        v-show="jobs.length === 0"
        class="my-10 flex w-full items-center justify-center gap-4 text-blue-dark-800 sm:my-20"
      >
        <MagnifyingLensIcon class="w-[1.125rem] shrink-0" />
        <p class="text-md text-blue-dark-800">
          {{ $t('filter.no_entries_found') }}
        </p>
      </div>
    </transition>
  </Container>
</template>

<script setup>
const props = defineProps({
  data: { type: Object, default: null },
})

const { locale } = useI18n()
const route = useRoute()
const params = useUrlSearchParams('history')

const jobs = ref([])
const sections = ref([])
const locations = ref([])
const types = ref([])

const hasMorePages = ref(null)
const limit = ref(8)

const isShowingMore = ref(false)

// POPULATE THE FILTER
let routeQueryFilter = route.query[`job-overview-${props.data.id}`]
if (routeQueryFilter) {
  routeQueryFilter = JSON.parse(routeQueryFilter)
}

// Could be that the route has ha filter query when routed from a job page TypeTag
const initialFilterSection = route.query.section
  ? [JSON.parse(route.query.section)]
  : []
const initialFilterLocation = route.query.location
  ? [JSON.parse(route.query.location)]
  : []
const initialFilterType = route.query.type ? [JSON.parse(route.query.type)] : []

const filter = ref(
  routeQueryFilter || {
    section: initialFilterSection,
    location: initialFilterLocation,
    type: initialFilterType,
    text: null,
    sort: 'date desc',
  }
)

// WATCHERS
watch(
  filter,
  (newValue) => {
    params[`job-overview-${props.data.id}`] = JSON.stringify(newValue)
    fetchJobs()
  },
  { deep: true }
)

// FUNCTIONS
function showMore() {
  limit.value += 8
  fetchJobs()
}

function GenerateQueryFilter() {
  const queryFilter = {
    site: locale.value,
  }

  Object.keys(filter.value).forEach((key) => {
    if (key === 'sort') {
      // this property is not in the filter query
      return
    }

    if (key === 'text') {
      queryFilter['title'] = {
        contains: filter.value[key],
      }
      return
    }

    queryFilter[key] = switchContainsOrIn(filter.value[key])
  })

  return queryFilter
}

async function fetchJobs() {
  let payload = await useAsyncGql({
    operation: 'Jobs',
    variables: {
      sort: filter.value.sort,
      limit: limit.value,
      filter: GenerateQueryFilter(),
    },
  })

  payload = payload.data.value?.entries

  hasMorePages.value = payload.has_more_pages

  if (!payload) {
    console.error('Jobs could not be fetched')
    return
  }

  jobs.value = payload.data
}

// INITIAL
let payload = await useAsyncGql({
  operation: 'JobsAndTerms',
  variables: {
    sort: filter.value.sort,
    limit: limit.value,
    filter: GenerateQueryFilter(),
    filterLocale: {
      site: locale.value,
    },
  },
})

payload = payload.data?.value

if (!payload) {
  console.error('Jobs could not be fetched')
}

sections.value = payload?.sections?.data

locations.value = payload?.locations?.data

types.value = payload?.types?.data

jobs.value = payload?.entries.data

hasMorePages.value = payload?.entries.has_more_pages

/*
  Rerender the overview if the filter is set in the url.
  his is needed because the ISR cache provides the data without the filter applied -> This results in a wrong data mix (e.g. image and name not corresponding)
  so wie need to rerender the overview with the filter applied.
*/
const rerenderKey = ref(0)
onMounted(() => {
  if (route.query[`job-overview-${props.data.id}`]) {
    rerenderKey.value++
  }
})
</script>
